<script setup lang="ts">
import type { formSetting } from 'loans/types/forms'
import StepCalculator from 'loans/components/form/steps/StepCalculator.client.vue'
import StepPaymentL20 from 'loans/components/form/steps/l20/StepPaymentL20.client.vue'
import StepPersonalL20 from 'loans/components/form/steps/l20/StepPersonalL20.client.vue'
import StepAddressL20 from 'loans/components/form/steps/l20/StepAddressL20.client.vue'

const formSettings = ref<formSetting>({ isLikeIframe: false, innerBorderColor: '', outerBorderColor: '' })
const route = useRoute()
const isFrame = !!useAttrs().frame
const store = useFormStore()
const step = ref(1)
// Checks if the page is in the storyblock
const sbApi = useStoryblokApiV2()
const data: Pick<StoryblokPage, 'data'>['data'] = await sbApi.getPage()
if (data) useStoryblokMeta(data)
else throw createError({ statusCode: 404 })

if (store.values.netIncome) step.value = 2
if (store.values.birthNumber) step.value = 3
if (store.values.street) step.value = 4

if (data.customPage.length) formSettings.value = data.customPage[0]
const { data: clientIp } = await useFetch('/api/ip')
store.values.ipAddress = clientIp.value?.ip || '127.0.0.1'
store.route = { path: route.path, query: route.query, fullPath: route.fullPath, hash: route.hash, params: route.params }
await store.getContentL20()

if (route.query.loanAmount) store.values.loanAmount = Number(route.query.loanAmount)
if (route.query.repayment) store.values.repayment = Number(route.query.repayment)

if (route.query.id) {
  store.values.id = (route.query.id as string).replace(/ /g, '+')
  await store.getDemand()
}
useAnalytics({
  event: 'calculator-form',
  event_type: 'calculator',
})
const dynamicCLasses = computed(() => {
  if (formSettings.value.isLikeIframe) {
    return {
      innerBorderColor: {
        'shadow-passive border': formSettings.value.isLikeIframe,
      },
      outerBorderColor: {
        '!mt-5 py-5 bg-white md:px-5': formSettings.value.isLikeIframe,
      },
    }
  }
  else {
    return {
      outerBorderColor: {
        'md:px-0': !formSettings.value.isLikeIframe,
      },
    }
  }
})
</script>

<template>
  <ClientOnly>
    <div v-if="!isFrame" class="pb-25" :class="{ 'bg-primary-pastel': !formSettings.isLikeIframe }">
      <StoryblokHeaderSimple :title="store.content.headerText" type="default" />
      <StoryblokRichText v-if="formSettings.isLikeIframeHeader && formSettings.isLikeIframe" :document="formSettings.isLikeIframeHeader" class="w-full px-5 mt-10 md:w-600px md:mx-auto" />
      <div
        class="md:w-600px md:mx-auto rounded-xl mt-10 px-5"
        :class="dynamicCLasses?.outerBorderColor"
      >
        <div
          class="flex flex-col bg-white rounded-xl md:w-full"
          :class="dynamicCLasses?.innerBorderColor"
        >
          <FormMain
            class="flex flex-col" :step="step"
            :content="store.content"
            :initial-values="store.values"
          >
            <StepCalculator :content="store.content" :calculator="store.calculatorSB" />
            <StepPaymentL20 :content="store.content" />
            <StepPersonalL20 :content="store.content" />
            <StepAddressL20 :content="store.content" />
          </FormMain>
        </div>
        <LoansFormProtection :loan-form-protection="store.content.loanFormProtection" />
      </div>
    </div>
    <div v-else class="pb-25 px-5">
      <div class="flex flex-col bg-white rounded-xl border mt-10 md:w-600px md:mx-auto">
        <FormMain
          class="flex flex-col" :step="Number(step)"
          :content="store.content"
          :initial-values="store.values"
        >
          <StepCalculator :content="store.content" :calculator="store.calculatorSB" />
          <StepPaymentL20 :content="store.content" />
          <StepPersonalL20 :content="store.content" />
          <StepAddressL20 :content="store.content" />
        </FormMain>
      </div>
      <LoansFormProtection :loan-form-protection="store.content.loanFormProtection" class="md:w-600px" />
    </div>
  </ClientOnly>
</template>
